import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { ENUM_PERMISSIONS } from '../../../enums/permission.enum';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import icClose from '@iconify/icons-ic/twotone-close';
import icDownload from '@iconify/icons-ic/twotone-download';
import * as XLSX from 'xlsx';

type PreviewType = 'pdf' | 'excel' | 'image' | 'unsupported';

interface ExcelSheetPreview {
  name: string;
  html: SafeHtml;
}

@Component({
  selector: 'vex-document-preview-modal',
  templateUrl: './document-preview-modal.component.html',
  styleUrls: ['./document-preview-modal.component.scss'],
})
export class DocumentPreviewModalComponent implements OnInit {
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  loading = false;
  icClose = icClose;
  icDownload = icDownload;
  fileBlob: Blob;
  fileName: string;

  url: SafeResourceUrl;
  previewType: PreviewType;
  excelSheets: ExcelSheetPreview[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { fileBlob: Blob; fileName: string },
    public dialogRef: MatDialogRef<DocumentPreviewModalComponent>,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;

    this.fileBlob = this.data.fileBlob;
    this.fileName = this.data.fileName;

    this.previewType = this.detectPreviewType(this.fileName);

    if (this.previewType === 'excel') {
      await this.loadExcelSheets();
    } else if (this.previewType === 'pdf' || this.previewType === 'image') {
      const blobUrl = URL.createObjectURL(this.fileBlob);
      this.url = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    }

    this.loading = false;
    this.cdr.detectChanges();
  }

  private detectPreviewType(fileName: string): PreviewType {
    const lower = fileName.toLowerCase();
    if (lower.endsWith('.pdf')) return 'pdf';
    if (/\.(xlsx?|csv|ods)$/.test(lower)) return 'excel';
    if (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower)) return 'image';
    return 'unsupported';
  }

  private async loadExcelSheets(): Promise<void> {
    try {
      const buffer = await this.fileBlob.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      this.excelSheets = workbook.SheetNames.map(name => {
        const sheet = workbook.Sheets[name];
        const html = XLSX.utils.sheet_to_html(sheet, { header: '', footer: '' });
        return {
          name,
          html: this.sanitizer.bypassSecurityTrustHtml(html),
        };
      });
    } catch (error) {
      console.error('Failed to parse spreadsheet file', error);
      this.previewType = 'unsupported';
    }
  }

  download(): void {
    const blobUrl = URL.createObjectURL(this.fileBlob);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = this.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(blobUrl);
  }

  close(result: any = null) {
    this.dialogRef.close(result);
  }
}
